import dayjs from 'dayjs';
import {
  HALF_SCORE_RESULT,
  ONE_HUNDRED_PERCENT,
  SCORE_INDEX
} from 'utils/constants/sportsbook/magic-numbers';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { dateFormat } from 'utils/date-manipulation/date-format';
import {
  EMatchStatuses,
  TMatch,
  TMatchStatistic,
  TMatchStatisticResponse,
  TPoint,
  TPopoverPositions,
  TStatistic,
  TStatisticTable,
  TTeam
} from 'interfaces/match-statistic';

const MATCHES_COUNT = 5;
const STATISTIC_POPOVER_HEIGHT = 394;

const ADDITIONAL_Y_POSITION_TO_TRIGGER_POPOVER_POSITION = 8;

const getIsHomeTeam = (homeTeamId: number, homeTeam: TTeam): boolean => {
  return homeTeamId === homeTeam.id;
};

const getMatchStatus = (
  teamScore: number,
  concurrentScore: number
): EMatchStatuses => {
  if (teamScore < concurrentScore) {
    return EMatchStatuses.lose;
  }

  if (teamScore > concurrentScore) {
    return EMatchStatuses.win;
  }

  return EMatchStatuses.draw;
};

const addMissingMatches = (matches: TStatistic[], result: TMatch[]) => {
  if (matches.length < MATCHES_COUNT) {
    for (let index = 0; index < MATCHES_COUNT - matches.length; index++) {
      result.push({
        id: index,
        score: '-',
        status: EMatchStatuses.draw
      });
    }
  }
};

const createH2HMatches = (teamId: number, matches: TStatistic[]) => {
  const result = matches.map(({ Id, HomeTeam, HomeScore, AwayScore }) => {
    const isHomeTeam = getIsHomeTeam(teamId, HomeTeam[0]);

    const homeScore = isHomeTeam ? HomeScore : AwayScore;
    const awayScore = isHomeTeam ? AwayScore : HomeScore;

    return {
      id: Id,
      score: `${homeScore}:${awayScore}`,
      status: getMatchStatus(homeScore, awayScore)
    };
  });

  addMissingMatches(matches, result);

  return result;
};

const createMatches = (teamId: number, matches: TStatistic[]) => {
  const result = matches.map(
    ({ Id, HomeTeam, AwayTeam, HomeScore, AwayScore }) => {
      const isHomeTeam = getIsHomeTeam(teamId, HomeTeam[0]);

      const homeScore = isHomeTeam ? HomeScore : AwayScore;
      const awayScore = isHomeTeam ? AwayScore : HomeScore;

      return {
        id: Id,
        score: `${HomeScore}:${AwayScore}`,
        status: getMatchStatus(homeScore, awayScore),
        teams: `${HomeTeam[0].name} - ${AwayTeam[0].name}`
      };
    }
  );

  addMissingMatches(matches, result);

  return result;
};

const findTeamFromLeague = (
  teamId: number,
  league: TStatisticTable[]
): TStatisticTable | undefined => {
  return league.find(({ EntityId }) => EntityId === teamId);
};

const formatNextMatchDate = (date?: string): string => {
  if (!date) {
    return '-';
  }

  return dayjs(date).format(
    dateFormat({
      date: SpringConfigs.DT.longDate
    })
  );
};

const calculateH2HResult = (
  teamId: number,
  matches: TStatistic[],
  type: 'all' | 'h' | 'a'
): TPoint => {
  if (!matches.length) {
    return '-';
  }

  let matchesCount = 0;
  let win = 0;
  let draw = 0;

  matches.forEach(({ HomeTeam, HomeScore, AwayScore }) => {
    const isHomeTeam = getIsHomeTeam(teamId, HomeTeam[0]);

    const homeScore = isHomeTeam ? HomeScore : AwayScore;
    const awayScore = isHomeTeam ? AwayScore : HomeScore;

    const calculateWinAndDraw = () => {
      if (homeScore > awayScore) {
        win++;
      }

      if (homeScore === awayScore) {
        draw++;
      }
    };

    if (type === 'all') {
      matchesCount++;

      calculateWinAndDraw();
    }

    if (type === 'h' && isHomeTeam) {
      matchesCount++;

      calculateWinAndDraw();
    }

    if (type === 'a' && !isHomeTeam) {
      matchesCount++;

      calculateWinAndDraw();
    }
  });

  const result =
    Math.round(
      (((win * 1 + draw * HALF_SCORE_RESULT) * ONE_HUNDRED_PERCENT) /
        matchesCount) *
        SCORE_INDEX
    ) / SCORE_INDEX;

  return `${isNaN(result) ? 0 : result}%`;
};

const calculateIt = (teamId: number, matches: TStatistic[]): TPoint => {
  if (!matches.length) {
    return '-';
  }

  const score = matches.reduce((result, { HomeTeam, HomeScore, AwayScore }) => {
    const isHomeTeam = getIsHomeTeam(teamId, HomeTeam[0]);

    result += isHomeTeam ? HomeScore : AwayScore;

    return result;
  }, 0);

  return `${Math.round((score / matches.length) * SCORE_INDEX) / SCORE_INDEX}`;
};

const calculateGt = (matches: TStatistic[]): TPoint => {
  if (!matches.length) {
    return '-';
  }

  const score = matches.reduce((result, { HomeScore, AwayScore }) => {
    result += HomeScore + AwayScore;

    return result;
  }, 0);

  return `${Math.round((score / matches.length) * SCORE_INDEX) / SCORE_INDEX}`;
};

export const getCompetitionLogoUrl = (id: number): string => {
  return `https://statistics.bcapps.org/images/c/s/0/${id}.png`;
};

export const getMatchStatistic = async (
  matchId: number
): Promise<TMatchStatisticResponse> => {
  const response = await fetch(
    `https://krosstats.betconstruct.com/api/${SpringConfigs.STATISTICS_LANG_PREFIX}/900/93f428d0-6591-48da-859d-b6c326db2448/Match/GetFullMatchInfo?matchId=${matchId}`
  );

  return await response.json();
};

export const formatMatchStatistic = ({
  HomeTeamId,
  AwayTeamId,
  CurrentMatch,
  H2HMatches,
  H2HleagueTable,
  UpcomingMatchesEnt1,
  UpcomingMatchesEnt2,
  PlayedMatchesEnt1,
  PlayedMatchesEnt2
}: TMatchStatisticResponse): TMatchStatistic => {
  const statistic: Partial<TMatchStatistic> = {};

  const homeTeamInLeague = findTeamFromLeague(HomeTeamId, H2HleagueTable);
  const awayTeamInLeague = findTeamFromLeague(AwayTeamId, H2HleagueTable);

  const homeTeam = {
    id: HomeTeamId,
    name: CurrentMatch.HomeTeamName
  };

  const awayTeam = {
    id: AwayTeamId,
    name: CurrentMatch.AwayTeamName
  };

  statistic.competition = {
    id: CurrentMatch.CompetitionId,
    name: CurrentMatch.CompetitionName
  };

  statistic.h2h = [
    {
      team: homeTeam,
      matches: createH2HMatches(HomeTeamId, H2HMatches),
      all: calculateH2HResult(HomeTeamId, H2HMatches, 'all'),
      h: calculateH2HResult(HomeTeamId, H2HMatches, 'h'),
      a: calculateH2HResult(HomeTeamId, H2HMatches, 'a'),
      it: calculateIt(HomeTeamId, H2HMatches),
      gt: calculateGt(H2HMatches)
    },
    {
      team: awayTeam,
      matches: createH2HMatches(AwayTeamId, H2HMatches),
      all: calculateH2HResult(AwayTeamId, H2HMatches, 'all'),
      h: calculateH2HResult(AwayTeamId, H2HMatches, 'h'),
      a: calculateH2HResult(AwayTeamId, H2HMatches, 'a'),
      it: calculateIt(AwayTeamId, H2HMatches)
    }
  ];

  statistic.leagueResults = [
    {
      team: homeTeam,
      pos: `${homeTeamInLeague?.PositionTotal || '-'}`,
      pld: `${homeTeamInLeague?.PlayedTotal || '-'}`,
      pts: `${homeTeamInLeague?.PointsTotal || '-'}`,
      nextMatch: formatNextMatchDate(UpcomingMatchesEnt1[0]?.Date)
    },
    {
      team: awayTeam,
      pos: `${awayTeamInLeague?.PositionTotal || '-'}`,
      pld: `${awayTeamInLeague?.PlayedTotal || '-'}`,
      pts: `${awayTeamInLeague?.PointsTotal || '-'}`,
      nextMatch: formatNextMatchDate(UpcomingMatchesEnt2[0]?.Date)
    }
  ];

  statistic.lastGames = [
    {
      team: homeTeam,
      matches: createMatches(HomeTeamId, PlayedMatchesEnt1),
      it: calculateIt(HomeTeamId, PlayedMatchesEnt1),
      gt: calculateGt(PlayedMatchesEnt1)
    },
    {
      team: awayTeam,
      matches: createMatches(AwayTeamId, PlayedMatchesEnt2),
      it: calculateIt(AwayTeamId, PlayedMatchesEnt2),
      gt: calculateGt(PlayedMatchesEnt2)
    }
  ];

  return statistic as TMatchStatistic;
};

export const getPopoverPlacement = (
  triggerId: string
): TPopoverPositions | null => {
  const trigger = document.querySelector(
    `[data-popover-trigger="${triggerId}"]`
  ) as HTMLElement;

  const clientTop = trigger?.getBoundingClientRect()?.top;

  const xPos = SpringConfigs.IS_RTL ? 'Right' : 'Left';
  const yPos =
    clientTop -
      STATISTIC_POPOVER_HEIGHT -
      ADDITIONAL_Y_POSITION_TO_TRIGGER_POPOVER_POSITION <
    0
      ? 'bottom'
      : 'top';

  return (yPos + xPos) as TPopoverPositions;
};
